import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { useMemo, useRef, useState } from "react";
import { apiJson } from "../lib/queryClient";
import { exportLogbookEntryPdf } from "../lib/pdfExport";
import TrackMap, { type TrackPoint } from "../components/TrackMap";
import {
  resolveFlightTimeDocuments,
  type PilotProfileForResolve,
  type ResolvedPilotDocument,
} from "@shared/archivedDocuments";

type DjiLogMeta = {
  status: "parsed" | "undecryptable" | "error";
  fileName: string;
  objectPath: string;
  sizeBytes?: number;
  uploadedAt?: string;
  parsedAt?: string;
  version?: number;
  droneName?: string;
  maxAltitudeM?: number;
  durationSec?: number;
  totalDistanceM?: number;
  batteryStartPercent?: number;
  batteryEndPercent?: number;
  batteryMinPercent?: number;
  trackPointCount?: number;
  message?: string;
};

type WeatherSnapshot = {
  temperature: number;
  windSpeed: number;
  windGust?: number;
  rain: number;
  cloudCover: number;
  humidity: number;
  kpIndex?: number;
};

type TrafficLogAircraft = {
  icao24: string;
  callsign: string;
  firstSeen: number;
  lastSeen: number;
  minAltitudeM: number;
  maxAltitudeM: number;
  closestDistanceKm: number;
  zone: "inner" | "outer" | "approach";
};

type TrafficLog = {
  startTime: number;
  endTime: number | null;
  centerLatitude: number;
  centerLongitude: number;
  innerRadiusM: number;
  outerRadiusM: number;
  aircraft: TrafficLogAircraft[];
  mapSnapshotBase64?: string;
};

type LogbookEntryFull = {
  id: string;
  droneName: string;
  droneClass: string;
  flightDate: string;
  locationName: string | null;
  takeoffTime: string | null;
  landingTime: string | null;
  flightDurationMinutes: number | null;
  maxAltitude: number | null;
  incidentOccurred: boolean;
  notes: string | null;
  latitude?: number | null;
  longitude?: number | null;
  countryIso?: string | null;
  weatherConditions?: WeatherSnapshot | null;
  gpsTrack?: TrackPoint[] | null;
  trafficLog?: TrafficLog | TrafficLogEntryWeb[] | null | unknown;
  djiLog?: DjiLogMeta | null;
  bundesland?: string | null;
  planning?: { officialMapCrossCheck?: { blocking: boolean; note?: string } } | null;
  wildlifeRescueAtFlight?: boolean | null;
  wildlifeRescueOrdinanceIds?: string[] | null;
};

type TrafficLogEntryWeb = {
  ts: number;
  icao24: string;
  callsign: string;
  distanceKm: number;
  altitudeM: number | null;
  bearingDeg: number;
  direction: string;
  level: "warning" | "critical";
};

type Profile = {
  name: string;
  pilotId: string;
  operatorId: string;
  photoUrl?: string | null;
} & PilotProfileForResolve;

const ZONE_LABEL: Record<TrafficLogAircraft["zone"], string> = {
  inner: "Inner",
  outer: "Outer",
  approach: "Annäherung",
};
const ZONE_COLOR: Record<TrafficLogAircraft["zone"], string> = {
  inner: "text-[var(--color-nogo)]",
  outer: "text-[var(--color-caution)]",
  approach: "text-[var(--color-app-text)]",
};

function fmtTs(ts: number) {
  return new Date(ts).toLocaleString("de-DE");
}

export default function LogbookDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: entries = [], isLoading } = useQuery<LogbookEntryFull[]>({ queryKey: ["/api/logbook"] });
  const { data: profile } = useQuery<Profile | null>({ queryKey: ["/api/profile"] });
  const { data: settings } = useQuery<{ attachPilotDocsToExports?: boolean; wildlifeRescueMode?: boolean } | null>({
    queryKey: ["/api/settings"],
  });
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const entry = entries.find((e) => e.id === id);

  const resolvedDocs = useMemo<ResolvedPilotDocument[]>(() => {
    if (!entry || !profile) return [];
    return resolveFlightTimeDocuments(profile, {
      flightDateISO: entry.flightDate,
      bundesland: entry.bundesland ?? null,
    });
  }, [entry, profile]);

  async function handleDjiUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !entry) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { uploadURL } = await apiJson<{ uploadURL: string }>("POST", "/api/objects/upload");
      const put = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "text/plain" },
      });
      if (!put.ok) throw new Error("Upload ins Object Storage fehlgeschlagen");
      await apiJson("POST", `/api/logbook/${entry.id}/dji-log`, {
        uploadURL,
        fileName: file.name,
        sizeBytes: file.size,
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/logbook"] });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : "Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  }

  if (isLoading) return <div className="text-center text-[var(--color-app-text-secondary)] py-10">Lädt …</div>;
  if (!entry) return <div className="card text-center py-12 text-[var(--color-app-text-secondary)]">Eintrag nicht gefunden.</div>;

  function handlePdf() {
    // Wildtierretter-Status primär aus dem Eintrag (Snapshot vom Flugzeitpunkt).
    // Nur Altdaten ohne Snapshot fallen auf die aktuelle Live-Einstellung zurück.
    const hasSnapshot = entry!.wildlifeRescueAtFlight != null;
    const wildlifeRescueMode = hasSnapshot
      ? !!entry!.wildlifeRescueAtFlight
      : settings?.wildlifeRescueMode ?? false;
    const ordinanceIds = hasSnapshot ? entry!.wildlifeRescueOrdinanceIds : null;
    let includeDocs = (settings?.attachPilotDocsToExports ?? true) && (resolvedDocs.length > 0 || wildlifeRescueMode);
    if (resolvedDocs.length > 0 || wildlifeRescueMode) {
      const message = wildlifeRescueMode
        ? "Hinterlegte Pilotendokumente (zum Flugzeitpunkt gültig) und die Allgemeinverfügungen zur Wildtierrettung an das PDF anhängen?\n\nOK = mit Dokumenten · Abbrechen = ohne"
        : "Hinterlegte Pilotendokumente (zum Flugzeitpunkt gültig) an das PDF anhängen?\n\nOK = mit Dokumenten · Abbrechen = ohne";
      includeDocs = window.confirm(message);
    }
    exportLogbookEntryPdf(
      {
        ...entry!,
        pilotName: profile?.name || null,
        pilotId: profile?.pilotId || null,
        operatorId: profile?.operatorId || null,
        pilotPhotoUrl: profile?.photoUrl || null,
      },
      includeDocs ? resolvedDocs : undefined,
      includeDocs && wildlifeRescueMode,
      ordinanceIds,
    );
  }

  const weather = entry.weatherConditions || null;
  const track: TrackPoint[] = Array.isArray(entry.gpsTrack) ? entry.gpsTrack : [];
  const trafficRaw: unknown = entry.trafficLog || null;
  const trafficEntries: TrafficLogEntryWeb[] | null =
    Array.isArray(trafficRaw) ? (trafficRaw as TrafficLogEntryWeb[]) : null;
  const trafficSession: TrafficLog | null =
    trafficRaw && !Array.isArray(trafficRaw) && typeof trafficRaw === "object" && "aircraft" in (trafficRaw as object)
      ? (trafficRaw as TrafficLog)
      : null;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/logbuch" className="text-sm text-brand-600 hover:underline">← Zurück zum Logbuch</Link>
        <div className="flex items-start justify-between mt-2 gap-3">
          <div>
            <p className="text-[var(--color-app-text-secondary)] text-sm">{entry.flightDate} · {entry.droneName}</p>
          </div>
          <button onClick={handlePdf} className="btn-primary" data-testid="button-export-pdf">PDF Export</button>
        </div>
      </div>

      <div className="card">
        <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Drohne</h2>
        <Row label="Name" value={entry.droneName} />
        <Row label="Klasse" value={entry.droneClass} />
      </div>

      <div className="card">
        <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Flug</h2>
        <Row label="Datum" value={entry.flightDate} />
        <Row label="Ort" value={entry.locationName || "—"} />
        {entry.latitude && entry.longitude ? (
          <Row label="Koordinaten" value={`${entry.latitude.toFixed(5)}, ${entry.longitude.toFixed(5)}`} />
        ) : null}
        <Row label="Land" value={entry.countryIso || "—"} />
        <Row label="Start" value={entry.takeoffTime ? new Date(entry.takeoffTime).toLocaleString("de-DE") : "—"} />
        <Row label="Landung" value={entry.landingTime ? new Date(entry.landingTime).toLocaleString("de-DE") : "—"} />
        <Row label="Dauer" value={entry.flightDurationMinutes ? `${entry.flightDurationMinutes} min` : "—"} />
        <Row label="Max. Höhe" value={entry.maxAltitude ? `${entry.maxAltitude} m AGL` : "—"} />
        <Row
          label="Vorfall"
          value={entry.incidentOccurred ? "Ja" : "Nein"}
          highlight={entry.incidentOccurred}
        />
      </div>

      {entry.planning?.officialMapCrossCheck ? (
        <div
          className="card"
          data-testid="box-crosscheck"
          style={{
            borderLeft: `4px solid ${entry.planning.officialMapCrossCheck.blocking ? "var(--color-caution)" : "var(--color-go)"}`,
          }}
        >
          <p
            className="text-sm font-semibold"
            style={{ color: entry.planning.officialMapCrossCheck.blocking ? "var(--color-caution)" : "var(--color-go)" }}
          >
            Offizielle Karte gegengeprüft:{" "}
            {entry.planning.officialMapCrossCheck.blocking
              ? "startverhindernde Geozonen"
              : "keine startverhindernden Geozonen"}
          </p>
          {entry.planning.officialMapCrossCheck.note ? (
            <p className="text-sm text-[var(--color-app-text-secondary)] mt-1">
              Notiz: {entry.planning.officialMapCrossCheck.note}
            </p>
          ) : null}
        </div>
      ) : null}

      {resolvedDocs.length > 0 ? (
        <div className="card" data-testid="section-docs">
          <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Hinterlegte Dokumente</h2>
          <p className="text-xs text-[var(--color-app-text-secondary)] mb-3">
            Zum Flugzeitpunkt gültige Pilotendokumente. Werden auf Wunsch dem PDF-Export beigefügt.
          </p>
          <div className="space-y-2">
            {resolvedDocs.map((doc, i) => (
              <div
                key={`${doc.type}-${doc.bundesland ?? ""}-${i}`}
                data-testid={`row-doc-${doc.type}`}
                className="flex items-center justify-between gap-3 py-1"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[var(--color-app-text)] truncate">{doc.label}</p>
                  <p className="text-xs text-[var(--color-app-text-secondary)]">
                    {doc.matchedExactly ? "Zum Flugzeitpunkt gültige Fassung" : "Bestmögliche Fassung"}
                    {doc.expirationDate ? ` · gültig bis ${doc.expirationDate}` : ""}
                    {doc.source === "archive" ? " · archiviert" : ""}
                  </p>
                </div>
                {doc.document?.objectPath ? (
                  <a
                    href={doc.document.objectPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 px-3 py-1 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:opacity-80 transition"
                    data-testid={`link-doc-${doc.type}`}
                  >
                    Ansehen
                  </a>
                ) : doc.type === "a2" ? (
                  <span className="shrink-0 text-xs text-[var(--color-app-text-secondary)]">Nur Metadaten</span>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="card" data-testid="section-weather">
        <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Wetter-Snapshot</h2>
        {weather ? (
          <>
            <Row label="Temperatur" value={`${weather.temperature.toFixed(1)} °C`} />
            <Row label="Wind" value={`${weather.windSpeed.toFixed(1)} m/s`} />
            <Row label="Böen" value={weather.windGust != null ? `${weather.windGust.toFixed(1)} m/s` : "—"} />
            <Row label="Niederschlag" value={`${weather.rain ?? 0} mm/h`} />
            <Row label="Bewölkung" value={`${weather.cloudCover ?? 0} %`} />
            <Row label="Luftfeuchte" value={`${weather.humidity ?? 0} %`} />
            {weather.kpIndex != null ? <Row label="Kp-Index" value={`${weather.kpIndex}`} /> : null}
          </>
        ) : (
          <p className="text-sm text-[var(--color-app-text-secondary)]">Kein Wetter-Snapshot für diesen Flug gespeichert.</p>
        )}
      </div>

      <div className="card" data-testid="section-track">
        <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">GPS-Track</h2>
        {track.length >= 2 ? (
          <>
            <p className="text-xs text-[var(--color-app-text-secondary)] mb-2">{track.length} Punkte aufgezeichnet</p>
            <TrackMap key={`track-${entry.id}-${track.length}`} track={track} fallbackLat={entry.latitude} fallbackLon={entry.longitude} />
          </>
        ) : entry.latitude != null && entry.longitude != null ? (
          <>
            <p className="text-xs text-[var(--color-app-text-secondary)] mb-2">Kein detaillierter Track verfügbar — Startposition wird angezeigt.</p>
            <TrackMap key={`fallback-${entry.id}`} track={[]} fallbackLat={entry.latitude} fallbackLon={entry.longitude} />
          </>
        ) : (
          <p className="text-sm text-[var(--color-app-text-secondary)]">Keine GPS-Daten für diesen Flug gespeichert.</p>
        )}
      </div>

      <div className="card" data-testid="section-dji-log">
        <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">DJI-Flugprotokoll</h2>
        {entry.djiLog ? (
          <>
            {entry.djiLog.status === "parsed" ? (
              <p className="text-xs text-[var(--color-go)] mb-3">
                Protokoll erfolgreich ausgewertet
                {entry.djiLog.trackPointCount ? ` · ${entry.djiLog.trackPointCount} GPS-Punkte` : ""}.
              </p>
            ) : entry.djiLog.status === "undecryptable" ? (
              <p className="text-xs text-[var(--color-caution)] mb-3" data-testid="text-dji-notice">
                Dieses Protokoll ist verschlüsselt (DJI v13+) und konnte ohne DJI-API-Schlüssel nicht
                vollständig ausgewertet werden. Die Datei bleibt als Download erhalten.
                {entry.djiLog.message ? ` ${entry.djiLog.message}` : ""}
              </p>
            ) : (
              <p className="text-xs text-[var(--color-nogo)] mb-3" data-testid="text-dji-notice">
                Protokoll konnte nicht ausgewertet werden. Die Datei bleibt als Download erhalten.
                {entry.djiLog.message ? ` ${entry.djiLog.message}` : ""}
              </p>
            )}
            {entry.djiLog.droneName ? <Row label="Drohne (Log)" value={entry.djiLog.droneName} /> : null}
            {entry.djiLog.version != null ? <Row label="Log-Version" value={`v${entry.djiLog.version}`} /> : null}
            {entry.djiLog.maxAltitudeM != null ? (
              <Row label="Max. Höhe (Log)" value={`${entry.djiLog.maxAltitudeM.toFixed(1)} m`} />
            ) : null}
            {entry.djiLog.durationSec != null ? (
              <Row label="Flugdauer (Log)" value={`${Math.round(entry.djiLog.durationSec)} s`} />
            ) : null}
            {entry.djiLog.totalDistanceM != null ? (
              <Row label="Strecke (Log)" value={`${(entry.djiLog.totalDistanceM / 1000).toFixed(2)} km`} />
            ) : null}
            {entry.djiLog.batteryStartPercent != null || entry.djiLog.batteryEndPercent != null || entry.djiLog.batteryMinPercent != null ? (
              <Row
                label="Akku (Start/Ende/Min)"
                value={[
                  entry.djiLog.batteryStartPercent != null ? `${entry.djiLog.batteryStartPercent}%` : "—",
                  entry.djiLog.batteryEndPercent != null ? `${entry.djiLog.batteryEndPercent}%` : "—",
                  entry.djiLog.batteryMinPercent != null ? `${entry.djiLog.batteryMinPercent}%` : "—",
                ].join(" / ")}
              />
            ) : null}
            <a
              href={entry.djiLog.objectPath}
              download={entry.djiLog.fileName}
              className="inline-block mt-3 text-sm text-brand-600 hover:underline"
              data-testid="link-dji-download"
            >
              {entry.djiLog.fileName} herunterladen
            </a>
            <div className="mt-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="btn-secondary text-sm"
                data-testid="button-dji-replace"
              >
                {uploading ? "Lädt hoch …" : "Anderes Protokoll hochladen"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-[var(--color-app-text-secondary)] mb-3">
              Lade ein DJI-Controller-Flugprotokoll (.txt aus DJI Fly/GO/Pilot) hoch, um GPS-Track,
              Höhe, Dauer und Akkudaten automatisch zu übernehmen.
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="btn-primary text-sm"
              data-testid="button-dji-upload"
            >
              {uploading ? "Lädt hoch …" : "DJI-Protokoll hochladen"}
            </button>
          </>
        )}
        {uploadError ? <p className="text-xs text-[var(--color-nogo)] mt-2" data-testid="text-dji-error">{uploadError}</p> : null}
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,text/plain"
          onChange={handleDjiUpload}
          className="hidden"
          data-testid="input-dji-file"
        />
      </div>

      <div className="card" data-testid="section-traffic">
        <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Verkehrslog (ADS-B / OGN)</h2>
        {trafficSession ? (
          <>
            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
              <Row label="Aufzeichnung ab" value={fmtTs(trafficSession.startTime)} />
              <Row label="Aufzeichnung bis" value={trafficSession.endTime ? fmtTs(trafficSession.endTime) : "—"} />
              <Row label="Inner Radius" value={`${Math.round(trafficSession.innerRadiusM)} m`} />
              <Row label="Outer Radius" value={`${Math.round(trafficSession.outerRadiusM)} m`} />
            </div>
            {trafficSession.aircraft.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[var(--color-app-text-secondary)] border-b border-[var(--color-app-border)]">
                      <th className="py-1.5 pr-2">Callsign</th>
                      <th className="py-1.5 pr-2">ICAO</th>
                      <th className="py-1.5 pr-2">Zone</th>
                      <th className="py-1.5 pr-2">Min Distanz</th>
                      <th className="py-1.5 pr-2">Höhe (min/max)</th>
                      <th className="py-1.5 pr-2">Erstmals</th>
                      <th className="py-1.5">Zuletzt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trafficSession.aircraft.map((a) => (
                      <tr key={a.icao24} className="border-b border-[var(--color-app-border)]" data-testid={`row-traffic-${a.icao24}`}>
                        <td className="py-1.5 pr-2 font-medium text-[var(--color-app-text)]">{a.callsign || "—"}</td>
                        <td className="py-1.5 pr-2 font-mono text-[var(--color-app-text-secondary)]">{a.icao24}</td>
                        <td className={`py-1.5 pr-2 font-semibold ${ZONE_COLOR[a.zone]}`}>{ZONE_LABEL[a.zone]}</td>
                        <td className="py-1.5 pr-2">{a.closestDistanceKm.toFixed(2)} km</td>
                        <td className="py-1.5 pr-2">{Math.round(a.minAltitudeM)} – {Math.round(a.maxAltitudeM)} m</td>
                        <td className="py-1.5 pr-2 text-[var(--color-app-text-secondary)]">{fmtTs(a.firstSeen)}</td>
                        <td className="py-1.5 text-[var(--color-app-text-secondary)]">{fmtTs(a.lastSeen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[var(--color-app-text-secondary)]">Keine Luftfahrzeuge während des Fluges erfasst.</p>
            )}
          </>
        ) : trafficEntries && trafficEntries.length > 0 ? (
          <>
            <p className="text-xs text-[var(--color-app-text-secondary)] mb-2">
              {trafficEntries.length} Annäherungen ·{" "}
              {trafficEntries.filter((e) => e.level === "critical").length} kritisch ·{" "}
              {trafficEntries.filter((e) => e.level === "warning").length} Warnung
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[var(--color-app-text-secondary)] border-b border-[var(--color-app-border)]">
                    <th className="py-1.5 pr-2">Zeit</th>
                    <th className="py-1.5 pr-2">Callsign</th>
                    <th className="py-1.5 pr-2">ICAO</th>
                    <th className="py-1.5 pr-2">Stufe</th>
                    <th className="py-1.5 pr-2">Distanz</th>
                    <th className="py-1.5 pr-2">Höhe</th>
                    <th className="py-1.5">Richtung</th>
                  </tr>
                </thead>
                <tbody>
                  {[...trafficEntries].sort((a, b) => a.ts - b.ts).map((e, idx) => (
                    <tr key={`${e.icao24}-${e.ts}-${idx}`} className="border-b border-[var(--color-app-border)]" data-testid={`row-traffic-${e.icao24}-${idx}`}>
                      <td className="py-1.5 pr-2 text-[var(--color-app-text-secondary)]">{fmtTs(e.ts)}</td>
                      <td className="py-1.5 pr-2 font-medium text-[var(--color-app-text)]">{e.callsign || "—"}</td>
                      <td className="py-1.5 pr-2 font-mono text-[var(--color-app-text-secondary)]">{e.icao24}</td>
                      <td className={`py-1.5 pr-2 font-semibold ${e.level === "critical" ? "text-[var(--color-nogo)]" : "text-[var(--color-caution)]"}`}>
                        {e.level === "critical" ? "Kritisch" : "Warnung"}
                      </td>
                      <td className="py-1.5 pr-2">{e.distanceKm.toFixed(2)} km</td>
                      <td className="py-1.5 pr-2">{e.altitudeM != null ? Math.round(e.altitudeM) + " m" : "—"}</td>
                      <td className="py-1.5">{e.direction} ({Math.round(e.bearingDeg)}°)</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-sm text-[var(--color-app-text-secondary)]">Kein Verkehrslog für diesen Flug gespeichert.</p>
        )}
      </div>

      {entry.notes ? (
        <div className="card">
          <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Notizen</h2>
          <p className="text-sm text-[var(--color-app-text)] whitespace-pre-wrap">{entry.notes}</p>
        </div>
      ) : null}
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm py-1">
      <div className="text-[var(--color-app-text-secondary)]">{label}</div>
      <div className={`col-span-2 ${highlight ? "text-[var(--color-nogo)] font-semibold" : "text-[var(--color-app-text)]"}`}>{value}</div>
    </div>
  );
}
